/**
 * templateGuideContent — calibration-upload-guidance
 *
 * Typed guide content registry for the Template Details page's Adaptive
 * Guidance Overlay (TemplateGuideOverlay). Covers the Calibration Takes
 * panel: what makes a "Good" / "Flawed" / "Disqualifying" example video
 * useful, why the reason field matters, and how to read the calibration
 * status the system reports back.
 *
 * Mirrors studioGuideContent.ts's shape (StudioGuideEntry / resolver /
 * section-to-key map) but with a single-level content-resolution chain —
 * this surface only has one contextual signal, `activeSection` (driven by
 * useAnchorSpy over `data-guide-section` markers in CalibrationTakesPanel /
 * CalibrationTakeUploader / CalibrationTakeList) — there is no
 * focusedField / selectedTool / activePanel / activePhase concept here.
 *
 * The copy below is the DETAILED tier of the two-tier guidance system
 * (owner request, calibration-upload-guidance branch). The SHORT tier
 * (condensed inline copy on the upload panel itself) lives in
 * CalibrationTakesPanel.tsx (intro line + "Learn more") and
 * CalibrationTakeUploader.tsx (per-label helper line) — deliberately
 * shorter paraphrases of this content, not verbatim copies; see those
 * files' `LABEL_HELPER_TEXT` / intro-line constants. The canonical
 * long-form version of all of this lives in
 * docs/TEMPLATE_SETUP_GUIDE.md § "Building a strong template with example
 * videos".
 *
 * No `learnMoreUrl` is set on any entry here: as of this writing NO entry
 * in studioGuideContent.ts or evaluationGuideContent.ts sets one either
 * (the field exists on the type for future use, but the frontend has no
 * route that serves the markdown docs under docs/, so pointing at one
 * would be a broken link). This mirrors that (non-)convention rather than
 * inventing a new one.
 *
 * To add a new entry: add a key to TemplateGuideKey, write its content
 * object, and (if it should react to scroll/focus) add a
 * `data-guide-section="<key>"` marker in the relevant component — the
 * overlay picks it up automatically via resolveTemplateGuideContent().
 */

import type { StructuredGuideBody } from './guideContentTypes';
import { buildScoringAspectsBody } from './scoringAspects';

export interface TemplateGuideEntry {
  heading: string;
  body: string | StructuredGuideBody;
  learnMoreUrl?: string;
}

// ---------------------------------------------------------------------------
// Key type — single source of truth for valid registry keys
// ---------------------------------------------------------------------------

export type TemplateGuideKey =
  | 'calibration-overview'
  | 'calibration-good'
  | 'calibration-bad'
  | 'calibration-fail'
  | 'calibration-reason'
  | 'calibration-results'
  | 'what-is-measured';

// ---------------------------------------------------------------------------
// Content registry
// ---------------------------------------------------------------------------

export const TEMPLATE_GUIDE_CONTENT: Record<TemplateGuideKey, TemplateGuideEntry> = {

  // Registry-driven — see schemas/scoring_aspects.json. The coach framing
  // leads with the distinction that actually resolves confusion in practice:
  // body form only measures landmarks you annotated, economy of motion always
  // watches the whole body.
  'what-is-measured': {
    heading: 'What gets measured',
    body: buildScoringAspectsBody('coach'),
  },

  'calibration-overview': {
    heading: 'Example videos',
    body: {
      lead: "Your examples don't just show the system good form — they define exactly where the scoring lines fall.",
      points: [
        'Each line is drawn midway between your closest acceptable and closest unacceptable example.',
        'So the clips nearest your judgment call do the most work.',
        'Extreme examples leave a wide gap the system has to guess inside.',
      ],
      lookFor: 'The Good / Flawed / Disqualifying slots below.',
    },
  },

  'calibration-good': {
    heading: 'Good examples',
    body: {
      lead: "Performances you'd accept from an athlete without correcting anything.",
      points: [
        'Two minimum. A third unlocks precise tolerances instead of generous fallbacks.',
        "Your most valuable Good is the weakest one you'd still accept — it sets the lower edge of acceptable.",
        'Different athletes if you can: one body teaches proportions, not form.',
        'If your Goods disagree strongly on some aspect, no boundary can form there.',
      ],
    },
  },

  'calibration-bad': {
    heading: 'Flawed examples',
    body: {
      lead: "Performances you'd correct but still score — not bad enough to disqualify.",
      points: [
        "Most valuable: the mildest fault you'd actually mention to an athlete.",
        'Paired with your weakest Good, it brackets exactly where penalties begin.',
        "Tag which aspect is wrong so the evidence isn't applied to unrelated ones.",
      ],
    },
  },

  'calibration-fail': {
    heading: 'Disqualifying examples',
    body: {
      lead: "Performances that deserve a zero — the movement essentially wasn't performed.",
      points: [
        'Most valuable: the mildest performance you would still fail.',
        'Tag the aspect that fails — "facing the wrong way" is an orientation fault, not a movement fault.',
        "An untagged catastrophic clip feeds every measure, including ones it's actually fine on.",
        'If detection read the wrong lead side for this take, correct it directly on the take — left-right comparisons should use the side you confirm, not the guess.',
      ],
    },
  },

  'calibration-reason': {
    heading: 'Why this example',
    body: {
      lead: "Describe what's right or wrong in your own words — this reaches the athlete's feedback.",
      points: [
        'Name the specific fault: "trailing knee collapses inward on landing".',
        'Used to match detected problems back to your language.',
        'Borderline Goods are worth a note too: "acceptable, but the minimum depth I\'d allow".',
      ],
    },
  },

  'calibration-results': {
    heading: 'What the system reports back',
    body: {
      lead: 'After each upload, calibration re-runs and tells you what it could and couldn\'t learn.',
      points: [
        'Coach messages name what\'s missing — e.g. "add a 3rd correct take".',
        '"View-limited" means an aspect had no discriminating example.',
        "No boundary set isn't a failure — it means your examples don't separate there yet.",
        'Got a take\'s lead side or mistake tag wrong after uploading? Edit it directly — the pencil icon on any take reopens those settings and re-runs calibration automatically.',
      ],
    },
  },
};

// ---------------------------------------------------------------------------
// Section → guide key mapping (mirrors STUDIO_SECTION_TO_KEY's shape)
// ---------------------------------------------------------------------------

/**
 * Maps `data-guide-section` attribute values rendered in
 * CalibrationTakesPanel / CalibrationTakeUploader / CalibrationTakeList to
 * registry keys. Currently an identity map (the DOM attribute values were
 * chosen to match the registry keys directly) but kept as an explicit map —
 * not a raw cast — so a future rename of either side doesn't silently
 * desync, and so this mirrors STUDIO_SECTION_TO_KEY / SECTION_TO_KEY's shape
 * for the adaptiveGuideDrift.test.ts anti-drift guard.
 */
export const TEMPLATE_SECTION_TO_KEY: Record<string, TemplateGuideKey> = {
  'calibration-good': 'calibration-good',
  'calibration-bad': 'calibration-bad',
  'calibration-fail': 'calibration-fail',
  'calibration-reason': 'calibration-reason',
  'calibration-results': 'calibration-results',
};

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

/**
 * Resolves the correct guide entry from the current context state.
 *
 * Priority (highest first):
 *   1. activeSection — scroll-spy / focus signal (useAnchorSpy over
 *      data-guide-section markers)
 *   2. null           — fall back to 'calibration-overview'
 */
export function resolveTemplateGuideContent(
  activeSection?: string | null,
): TemplateGuideEntry {
  if (activeSection) {
    const sectionKey = TEMPLATE_SECTION_TO_KEY[activeSection];
    if (sectionKey && sectionKey in TEMPLATE_GUIDE_CONTENT) {
      return TEMPLATE_GUIDE_CONTENT[sectionKey];
    }
    // Try direct key lookup for section values that match registry keys.
    const directKey = activeSection as TemplateGuideKey;
    if (directKey in TEMPLATE_GUIDE_CONTENT) {
      return TEMPLATE_GUIDE_CONTENT[directKey];
    }
  }

  return TEMPLATE_GUIDE_CONTENT['calibration-overview'];
}
